import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { SCROLL_BOTTOM_OUT, SEARCH_DEBOUNCE_MILLIS } from "src/app/constants/list";
import { IntegrationLogEntry } from "src/app/models/integration-log-entry.model";
import { User } from "src/app/models/user.model";
import { IntegrationSetup } from "src/app/models/integration-setup.model";
import { integrationIcon } from "src/app/utils/icons";
import { formatXML } from "src/app/utils/format";
import { Subscription } from 'rxjs';

@Component({
    selector: "integration-report",
    templateUrl: "./integration-report.component.html",
    styleUrls: ["./integration-report.component.scss"],
    standalone: false
})
export class IntegrationReportComponent implements OnInit, OnDestroy {

    protected FILTER_SPAN_KEY = 'flt-intlog-when'

    /** define if is loading */ 
    protected loading: boolean = false

    /** Indicates the contents of a tab were recently copied to clipboard, For UI confirmation. */
    protected copied: boolean = false

    // save the language
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    // A list od user integration log entries for the report.
    protected entries: IntegrationLogEntry[] = []

    // A list of available integration setups for the company
    protected setups: IntegrationSetup[] = []

    // The currently selected lot
    protected lotId: number = 0

     // Current page the user is viewing (last loaded).
    protected page = 1

    // The number contacts currently being displayed.
    protected displayed = 0

    // Indicates whether or not there are no more records to load.
    protected exhausted = false

    // Total number of records available
    protected records: number

    // The search form group
    protected form: FormGroup | null = null

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> = null

    private subscriptions: Subscription[] = []

    // Keep track of how the user wants to sort list.
    private sortables: Object = {'createdAt': 'DESC'}

    // The integration entry whose meta data is currently being viewed.
    protected viewable: IntegrationLogEntry | null = null

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            span: new FormControl<string>('', []),
            from: new FormControl<string>('', []),
            to: new FormControl<string>('', []),
            setup: new FormControl<number>(0, [])
        })
    }

    ngOnInit() {
        this.lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)

        this.subscriptions.push(
            this.form.get('span').valueChanges.subscribe(span => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('setup').valueChanges.subscribe(user => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.readFilters()
        this.fetchSetups()
        this.fetch()
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    protected scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
     * 
     * @param field The field that user wants to sort by. 
     */
    protected sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables = {}
        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    protected sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    protected sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    /**
     * Query for a list of sales.
     * @param lot Unique identification of the selected lot
     */
    protected fetch = async () => {
        if (this.security.notPermitted('reports', 'integrations log', 'view')) {
            return
        }

        if (this.loading) { // already getting next page
            return
        }
  
        if (this.exhausted) { // currently showing all available records?
            return
        }
  
        this.loading = true

        let spanParam = ''
        const spanKey = this.form.get('span').value
        if (spanKey && (spanKey.length > 0)) {
            switch (spanKey) {
                case 'custom':
                    const from = this.form.get('from').value
                    const to = this.form.get('to').value
                    spanParam = `&from=${from}&to=${to}`
                    break
                default:
                    spanParam = `&span=${spanKey}`
                    break
            }
        }
  
        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }
  
                sorts.push(`${key}${direction}`)
            }
  
            sortParam = `&sort=${sorts.join(',')}`
        }

        let setupParam = ''
        const setup = parseInt(this.form.get('setup').value)
        if (setup && (!isNaN(setup)) && (setup > 0)) {
            setupParam = `&setupId=${setup}`
        }

        this.loading = true
        await this.master.getAsync(`reports/integration?page=${this.page}${sortParam}${spanParam}${setupParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.entries = this.entries.concat(res.data.entries)
                return
            }

            this.entries = res.data.entries
        })
    }

    /**
     * List integration setups for the currently selected lot.
     */
    private fetchSetups() {
        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        this.master.get(`lots/${this.lotId}/integrations`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setups = res.data.setups
        })
    }

    /**
     * Check session storage for stored filters and apply them if found.
     */
    private readFilters = () => {
        if (!sessionStorage) {
            return
        }

        const spanFilter = sessionStorage.getItem(this.FILTER_SPAN_KEY)
        if (spanFilter && (spanFilter.length > 0)) {
            if (spanFilter.startsWith('custom')) {
                const items = spanFilter.split(';')
                if (!items || (items.length !== 3)) {
                    return
                }

                this.form.get('span').setValue(items[0], {emitEvent: false})
                this.form.get('from').setValue(items[1], {emitEvent: false})
                this.form.get('to').setValue(items[2], {emitEvent: false})    

                return
            }

            this.form.get('span').setValue(spanFilter, {emitEvent: false})
        }
        else {
            this.form.get('span').setValue('today', {emitEvent: false})
            sessionStorage.setItem(this.FILTER_SPAN_KEY, `today`)
        }
    }

    /**
     * Save the existing list fitlers to session storage for later retrieval.
     */
    private writeFilters = () => {
        if (!sessionStorage) {
            return
        }

        const spanFilter = this.form.get('span').value
        if (!spanFilter || (spanFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_SPAN_KEY)
        }
        else {
            let fromTo = ''
            if (spanFilter === 'custom') {
                const from = this.form.get('from').value
                const to = this.form.get('to').value
                fromTo = `;${from};${to}`
            }

            sessionStorage.setItem(this.FILTER_SPAN_KEY, `${spanFilter}${fromTo}`)
        }
    }

    /**
     * Handle date span choices by the user.
     * @param choice 
     */
    protected spanned = (choice: Object) => {
        if (!choice || (choice['key'] === undefined) || (choice['key'] === null)) {
            return
        }

        if (choice['key'] === 'custom') {
            const from = choice['from']
            const to = choice['to']
            if (!from || !to) {
                return
            }

            this.form.get('from').setValue(from, {emitEvent: false})
            this.form.get('to').setValue(to, {emitEvent: false})    
        }

        this.form.get('span').setValue(choice['key'])
    }

    /**
     * 
     * @param entry 
     */
    protected view = (entry: IntegrationLogEntry) => {
        if (!entry || !entry.request) {
            this.viewable = null
            return
        }

        const btn = document.getElementById('btn-toggle-integration-meta-view') as HTMLButtonElement
        if (!btn) {
            this.viewable = null
            return
        }

        this.viewable = entry
        btn.click()
    }

     /**
     * Return integration log request when available.
     */
     protected request = () => {
        if (!this.viewable || !this.viewable.request) {
            return ''
        }

        switch (this.viewable.requestFormat) {
            case 'application/json':
                return JSON.stringify(JSON.parse(this.viewable.request), null, '\t')
            case 'text/xml':
            case 'application/soap+xml':
                return formatXML(this.viewable.request)
            default:
                return this.viewable.request
        }
    }

    /**
     * Return integration log response when available.
     */
     protected response = () => {
        if (!this.viewable || !this.viewable.response) {
            return ''
        }

        switch (this.viewable.responseFormat) {
            case 'application/json':
                return JSON.stringify(JSON.parse(this.viewable.response), null, '\t')
            case 'text/xml':
            case 'application/soap+xml':
                return formatXML(this.viewable.response)
            default:
                return this.viewable.response
        }
    }

    /**
     * Return integration log settings when available.
     */
     protected settings = () => {
        if (!this.viewable || !this.viewable.settings) {
            return ''
        }

        return JSON.stringify(JSON.parse(this.viewable.settings), null, '\t')
    }

    /**
     * 
     * @param status 
     */
    protected statusColor = (status: string) => {
        if (!status) {
            return 'none'
        }

        const code = parseInt(status)
        if (!code || (isNaN(code)) || (code <= 0)) {
            return 'none'
        }

        if ((code >= 200) && (code <= 299)) {
            return 'ok'
        }

        if ((code >= 400) && (code <= 499)) {
            return 'client'
        }

        if ((code >= 500) && (code <= 599)) {
            return 'server'
        }

        return 'none'
    }

    /**
     * 
     * @param category 
     */
    protected iconify = (category) => {
        return integrationIcon(category)
    }
    
    /**
     * listen to output of navbar
    */ 
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                this.lotId = parseInt(e['lot'])
                this.form.get('setup').setValue(0)
                this.fetchSetups()
                break
        }
    }

    /**
     * Copy the request or response contents to the clipboard based \
     * on the given type (request, response)
     * @param e Event that trigger the copy to clipboard
     * @param type One of request or resposne to tell the method which contents to copy. 
     */
    protected clip = (e: Event, type: string): void => {
        if (!navigator || !navigator.clipboard) {
            return
        }

        this.copied = true
        setTimeout(() => {
            this.copied = false
        }, 3000)

        if (type === 'request') {
            navigator.clipboard.writeText(this.request())
            return
        }

        navigator.clipboard.writeText(this.response())
    }
}
